import { useCallback, useEffect, useRef, useState } from 'react';

interface RetryConfig {
  maxRetries?: number;
  initialDelay?: number;
}

interface RetryableReturn<T> {
  execute: () => Promise<T | null>;
  isRetrying: boolean;
  retryCount: number;
  lastError: Error | null;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const MAX_RENDERS_PER_INTERVAL = 100;
const RENDER_CHECK_INTERVAL_MS = 5000;

export const useRetryableFetch = <T>(
  fetchFunction: () => Promise<T>,
  config: RetryConfig = {}
): RetryableReturn<T> => {
  const { maxRetries = 3, initialDelay = 1000 } = config;
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState<Error | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchFunctionRef = useRef(fetchFunction);
  const renderCountRef = useRef(0);
  const intervalStartTimeRef = useRef(Date.now());
  const infiniteLoopDetectedRef = useRef(false);

  useEffect(() => {
    fetchFunctionRef.current = fetchFunction;
  });

  renderCountRef.current++;
  const now = Date.now();
  if (now - intervalStartTimeRef.current > RENDER_CHECK_INTERVAL_MS) {
    renderCountRef.current = 1;
    intervalStartTimeRef.current = now;
    infiniteLoopDetectedRef.current = false;
  } else if (renderCountRef.current > MAX_RENDERS_PER_INTERVAL && !infiniteLoopDetectedRef.current) {
    console.error(
      `[useRetryableFetch] Infinite loop detected: ${renderCountRef.current} renders in ${now - intervalStartTimeRef.current}ms. ` +
      `Blocking further execute() calls until reset.`
    );
    infiniteLoopDetectedRef.current = true;
  }

  const execute = useCallback(async (): Promise<T | null> => {
    if (infiniteLoopDetectedRef.current) {
      console.warn('[useRetryableFetch] execute() blocked due to infinite loop detection');
      return null;
    }

    abortControllerRef.current = new AbortController();
    let currentRetry = 0;
    setRetryCount(0);
    setLastError(null);

    while (currentRetry <= maxRetries) {
      try {
        if (currentRetry > 0) {
          setIsRetrying(true);
          const delay = initialDelay * Math.pow(2, currentRetry - 1);
          console.log(`[useRetryableFetch] Retry ${currentRetry}/${maxRetries} in ${delay}ms...`);
          await sleep(delay);
        }

        const result = await fetchFunctionRef.current();

        if (currentRetry > 0) {
          console.log(`[useRetryableFetch] Retry successful after ${currentRetry} attempt(s)`);
        }

        setIsRetrying(false);
        setRetryCount(0);
        setLastError(null);
        return result;
      } catch (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        setLastError(errorObj);

        if (currentRetry === maxRetries) {
          console.error(`[useRetryableFetch] All retry attempts (${maxRetries}) failed:`, errorObj.message);
          setIsRetrying(false);
          setRetryCount(currentRetry);
          return null;
        }

        console.warn(`[useRetryableFetch] Attempt ${currentRetry + 1} failed:`, errorObj.message);
        currentRetry++;
        setRetryCount(currentRetry);
      }
    }

    setIsRetrying(false);
    return null;
  }, [maxRetries, initialDelay]);

  return {
    execute,
    isRetrying,
    retryCount,
    lastError,
  };
};
